import { Control, FieldErrors } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CollapseProps } from 'antd';
import { AimOutlined } from '@ant-design/icons';
import {
  DarwinCol,
  DarwinCollapse,
  //DarwinDivider,
  DarwinRow,
} from 'components/darwin-ui';
import DarwinTypography from 'components/darwin-ui/Typography';
import { GenericInput } from '../../GenericInput';
import styles from '../Organizations.module.css';
import { InputType } from 'types/genericTypes';
import {
  ORGANIZATION_TEXT_AREA_MAX_LENGTH,
  ORGANIZATION_NAME_MAX_LENGTH,
  ORGANIZATION_INPUT_MAX_LENGTH,
  ORGANIZATION_FISCAL_ID_MIN_LENGTH,
} from 'utils/organization';
import {
  OrganizationDetailSectionFormData,
  OrganizationInputProps,
} from 'types/organizationTypes';
//import { OrganizationKeyInput } from './OrganizationKeyInput';

const { Text } = DarwinTypography;

interface OrganizationDetailProps {
  control: Control<OrganizationDetailSectionFormData>;
  errors: FieldErrors<OrganizationDetailSectionFormData>;
  resetOrganizationDetailFormData: (
    data: OrganizationDetailSectionFormData
  ) => void;
  organizationKeyDefaultValue: string;
}

export const OrganizationDetailSection = ({
  control,
  errors,
  resetOrganizationDetailFormData,
  organizationKeyDefaultValue,
}: OrganizationDetailProps): JSX.Element => {
  const { t } = useTranslation();

  const firstColumnInputs: OrganizationInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'name',
      label: t('create-organization-form.organization-name'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.organization-name-required'),
        maxLength: {
          value: ORGANIZATION_NAME_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.organization-name'),
            maxLength: ORGANIZATION_NAME_MAX_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_NAME_MAX_LENGTH,
    },
    {
      inputType: InputType.TEXTAREA,
      fieldName: 'description',
      label: t('create-organization-form.description'),
      showRequiredAsterisk: false,
      inputRules: {
        maxLength: {
          value: ORGANIZATION_TEXT_AREA_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.description'),
            maxLength: ORGANIZATION_TEXT_AREA_MAX_LENGTH,
          }),
        },
      },
      showCount: true,
      maxLength: ORGANIZATION_TEXT_AREA_MAX_LENGTH,
    },
  ];

  const secondColumnInputs: OrganizationInputProps = [
    {
      inputType: InputType.INPUT,
      fieldName: 'fiscalId',
      label: t('create-organization-form.organization-fiscal-id'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('create-organization-form.organization-fiscal-id-required'),
        maxLength: {
          value: ORGANIZATION_INPUT_MAX_LENGTH,
          message: t('organization.create-organization.max-length-error', {
            fieldName: t('create-organization-form.organization-fiscal-id'),
            maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
          }),
        },
        minLength: {
          value: ORGANIZATION_FISCAL_ID_MIN_LENGTH,
          message: t('organization.create-organization.min-length-error', {
            fieldName: t('create-organization-form.organization-fiscal-id'),
            minLength: ORGANIZATION_FISCAL_ID_MIN_LENGTH,
          }),
        },
      },
      maxLength: ORGANIZATION_INPUT_MAX_LENGTH,
    },
  ];

  const items: CollapseProps['items'] = [
    {
      key: '1',
      label: (
        <Text className={styles.organizationFormRowTitle}>
          <AimOutlined className={styles.organizationFormTitleIcon} />
          {t('create-organization-form.organization-details')}
        </Text>
      ),
      children: (
        <div className={styles.organizationFormContainer}>
          {/* This section is being commented on by Demo realizations */}
          {/* <DarwinRow gutter={32}>
            <DarwinCol span={24}>
              <OrganizationKeyInput
                resetOrganizationDetailFormData={
                  resetOrganizationDetailFormData
                }
                organizationKeyDefaultValue={organizationKeyDefaultValue}
              />
            </DarwinCol>
          </DarwinRow>
          <DarwinDivider /> */}
          <DarwinRow gutter={32}>
            <DarwinCol span={12}>
              {firstColumnInputs.map(input => (
                <GenericInput
                  key={input.fieldName}
                  control={control}
                  errors={errors}
                  {...input}
                />
              ))}
            </DarwinCol>
            <DarwinCol span={12}>
              {secondColumnInputs.map(input => (
                <GenericInput
                  key={input.fieldName}
                  control={control}
                  errors={errors}
                  {...input}
                />
              ))}
            </DarwinCol>
          </DarwinRow>
        </div>
      ),
    },
  ];
  return (
    <>
      <DarwinCollapse
        defaultActiveKey={'1'}
        className={styles.organizationFormRow}
        items={items}
        expandIconPosition="end"
        size="small"
      />
    </>
  );
};
